import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, UserPlus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BG =
  "https://images.unsplash.com/photo-1688891648910-1e056d2377c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxub3JkaWMlMjBhYnN0cmFjdCUyMG5hdHVyZSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg1MTY1OTg3fDA&ixlib=rb-4.1.0&q=85";

export default function Register() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/hjem");
  }, [user, navigate]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!name || !email || !password) {
      setError("Fyll inn navn, e-post og passord.");
      return;
    }
    if (password.length < 8) {
      setError("Passordet må ha minst 8 tegn.");
      return;
    }
    if (password !== confirm) {
      setError("Passordene er ikke like.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      login(data.user);
      navigate("/hjem");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen bg-background">
      <div className="absolute right-5 top-5 z-20">
        <ThemeToggle />
      </div>

      <div className="flex w-full flex-col justify-center px-6 py-12 sm:px-12 lg:w-[46%] lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mx-auto w-full max-w-sm"
        >
          <Logo subtitle="Styrerom & Aksjeeierbok" />

          <div className="mt-10">
            <h1 className="font-headings text-3xl font-extrabold tracking-tight text-foreground">Opprett konto</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Registrer deg med e-post og passord. Du verifiserer identiteten din (BankID eller admin) før du legger til selskap.
            </p>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="name" className="text-xs font-medium">Fullt navn</Label>
              <Input id="name" data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11" placeholder="Ola Nordmann" />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs font-medium">E-post</Label>
              <Input id="email" data-testid="register-email-input" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" placeholder="din@epost.no" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="password" className="text-xs font-medium">Passord</Label>
                <Input id="password" data-testid="register-password-input" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11" placeholder="min. 8 tegn" />
              </div>
              <div>
                <Label htmlFor="confirm" className="text-xs font-medium">Bekreft</Label>
                <Input id="confirm" data-testid="register-password-confirm-input" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 h-11" placeholder="gjenta" />
              </div>
            </div>

            {error ? <p data-testid="register-error" className="text-sm text-destructive">{error}</p> : null}

            <Button type="submit" data-testid="register-submit-button" disabled={loading} className="h-11 w-full gap-2 text-sm font-semibold active:scale-[0.98]">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              {loading ? "Oppretter …" : "Opprett konto"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            Har du allerede konto?{" "}
            <Link to="/login" data-testid="register-login-link" className="font-semibold text-primary hover:underline">Logg inn</Link>
          </p>

          <p className="mt-10 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Lock className="h-3 w-3" /> Kryptert · ISO 27001 · Data lagret i Norge
          </p>
        </motion.div>
      </div>

      <div className="relative hidden lg:block lg:w-[54%]">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/70 via-primary/30 to-transparent" />
      </div>
    </div>
  );
}
